import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider,
  TextField, Typography,
} from '@mui/material'
import CheckCircleIcon  from '@mui/icons-material/CheckCircleOutlined'
import CancelIcon       from '@mui/icons-material/CancelOutlined'
import EventIcon        from '@mui/icons-material/EventOutlined'
import ChatIcon         from '@mui/icons-material/ChatBubbleOutlineOutlined'
import HourglassIcon    from '@mui/icons-material/HourglassEmptyOutlined'
import RestartAltIcon   from '@mui/icons-material/RestartAltOutlined'
import { supabase } from '../../supabaseClient'
import { useViewerStudent } from '../../auth/ViewAsContext'
import ConversationThread from '../../components/messaging/ConversationThread'

// Student-facing read of the Step 1 mentor review pipeline. Pulls from
// research_topic + mentor_review_log + meeting (the new system) and shows
// the current state, the latest mentor note, and a chronological log of
// actions taken so far. Staff get this same view for parity.

interface ReviewLogEntry {
  log_id:        string
  action:        'comment' | 'schedule' | 'mark_held' | 'approve'
  note:          string | null
  acted_at:      string
  reviewer_name: string | null
}

interface MeetingEntry {
  meeting_id:     string
  status:         'requested' | 'scheduled' | 'held' | 'cancelled'
  requested_at:   string
  scheduled_at:   string | null
  held_at:        string | null
  held_summary:   string | null
  cancel_reason:  string | null
  scheduled_note: string | null
  request_note:   string | null
}

interface RevisionRequestEntry {
  id:              string
  step_number:     number
  target_subphase: string | null
  reason:          string
  status:          'pending' | 'approved' | 'denied'
  mentor_note:     string | null
  requested_at:    string
  decided_at:      string | null
  mentor_name:     string | null
}

interface TopicData {
  status:                     'draft' | 'submitted' | 'completed' | string
  mentor_note:                string | null
  selected_recommendation_id: string | null
  finalist_recommendation_ids: string[] | null
  approvedTitle:              string | null
  approvedDescription:        string | null
  finalistTitles:             Record<string, string>
}

interface Props {
  projectId: string
  studentId: string
}

const ACTION_LABEL: Record<ReviewLogEntry['action'], string> = {
  comment:   'Sent back with comment',
  schedule:  'Scheduled / requested meeting',
  mark_held: 'Meeting held — notes recorded',
  approve:   'Approved final topic',
}

function fmtDateTime(s: string) {
  return new Date(s).toLocaleString('en-US', {
    weekday: 'short', month: 'long', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
    timeZone: 'America/Los_Angeles',
  })
}

export default function MentorSessionTab({ projectId, studentId }: Props) {
  const { viewerIsStudent } = useViewerStudent()
  const [topic,            setTopic]            = useState<TopicData | null>(null)
  const [logs,             setLogs]             = useState<ReviewLogEntry[]>([])
  const [meetings,         setMeetings]         = useState<MeetingEntry[]>([])
  const [revisionRequests, setRevisionRequests] = useState<RevisionRequestEntry[]>([])
  const [loading,          setLoading]          = useState(true)
  const [error,            setError]            = useState<string | null>(null)
  const [reqDialogOpen,    setReqDialogOpen]    = useState(false)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true); setError(null)
      try {
        const { data: rt, error: rtErr } = await supabase
          .from('research_topic')
          .select('id, status, mentor_note, selected_recommendation_id, finalist_recommendation_ids')
          .eq('project_id', projectId)
          .maybeSingle()
        if (rtErr) throw new Error(rtErr.message)
        if (cancelled) return

        const finalistIds = (rt?.finalist_recommendation_ids ?? []) as string[]
        const idsToLookup = Array.from(new Set([
          ...finalistIds,
          ...(rt?.selected_recommendation_id ? [rt.selected_recommendation_id] : []),
        ]))
        const { data: recs } = idsToLookup.length > 0
          ? await supabase
              .from('topic_recommendation')
              .select('id, title, description')
              .in('id', idsToLookup)
          : { data: [] as { id: string; title: string; description: string }[] }
        const titleMap: Record<string, string> = Object.fromEntries((recs ?? []).map(r => [r.id, r.title]))
        const approved = rt?.selected_recommendation_id
          ? (recs ?? []).find(r => r.id === rt.selected_recommendation_id)
          : null

        const [{ data: logRows }, { data: meetingRows }, { data: revRows }] = await Promise.all([
          supabase
            .from('mentor_review_log')
            .select('log_id, action, note, acted_at, reviewer_id')
            .eq('project_id', projectId)
            .order('acted_at', { ascending: true }),
          rt?.id
            ? supabase
                .from('meeting')
                .select('meeting_id, status, requested_at, scheduled_at, held_at, held_summary, cancel_reason, scheduled_note, request_note')
                .eq('research_topic_id', rt.id)
                .eq('kind', 'step1_review')
                .order('requested_at', { ascending: true })
            : Promise.resolve({ data: [] as MeetingEntry[] }),
          supabase
            .from('revision_request')
            .select('id, step_number, target_subphase, reason, status, mentor_note, requested_at, decided_at, mentor_id')
            .eq('project_id', projectId)
            .eq('step_number', 1)
            .order('requested_at', { ascending: true }),
        ])

        // Resolve reviewer names for the log + mentor names for revision requests
        const reviewerIds = Array.from(new Set([
          ...((logRows ?? []).map((l: any) => l.reviewer_id).filter(Boolean) as string[]),
          ...((revRows ?? []).map((r: any) => r.mentor_id).filter(Boolean) as string[]),
        ]))
        const { data: reviewers } = reviewerIds.length > 0
          ? await supabase.from('users').select('user_id, first_name, last_name').in('user_id', reviewerIds)
          : { data: [] as { user_id: string; first_name: string; last_name: string }[] }
        const reviewerMap = Object.fromEntries((reviewers ?? []).map(u => [
          u.user_id, `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || null,
        ]))

        if (cancelled) return
        setTopic({
          status:                       rt?.status ?? 'draft',
          mentor_note:                  rt?.mentor_note ?? null,
          selected_recommendation_id:   rt?.selected_recommendation_id ?? null,
          finalist_recommendation_ids:  finalistIds,
          approvedTitle:                approved?.title ?? null,
          approvedDescription:          approved?.description ?? null,
          finalistTitles:               titleMap,
        })
        setLogs(((logRows ?? []) as any[]).map(l => ({
          log_id:        l.log_id,
          action:        l.action,
          note:          l.note,
          acted_at:      l.acted_at,
          reviewer_name: l.reviewer_id ? (reviewerMap[l.reviewer_id] ?? null) : null,
        })))
        setMeetings(((meetingRows ?? []) as any[]) as MeetingEntry[])
        setRevisionRequests(((revRows ?? []) as any[]).map(r => ({
          id:              r.id,
          step_number:     r.step_number,
          target_subphase: r.target_subphase,
          reason:          r.reason,
          status:          r.status,
          mentor_note:     r.mentor_note,
          requested_at:    r.requested_at,
          decided_at:      r.decided_at,
          mentor_name:     r.mentor_id ? (reviewerMap[r.mentor_id] ?? null) : null,
        })))
      } catch (e: any) {
        if (!cancelled) setError(e.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [projectId])

  if (loading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
  if (error)   return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>

  if (!topic || (topic.status === 'draft' && !topic.mentor_note && logs.length === 0 && revisionRequests.length === 0)) {
    return (
      <Box sx={{ maxWidth: 720, py: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Mentor Review</Typography>
        <Typography variant="body2" color="text.secondary">
          No review activity yet. When you submit your finalists, your mentor's review and any
          meetings will be recorded here.
        </Typography>
      </Box>
    )
  }

  const pendingRevision = revisionRequests.find(r => r.status === 'pending') ?? null
  const canRequestRevision =
    viewerIsStudent
    && topic.status === 'completed'
    && !pendingRevision

  // Status badge derivation
  const activeMeeting = meetings.find(m => m.status === 'requested' || m.status === 'scheduled')
  const headlineStatus = (() => {
    if (topic.status === 'completed') return { label: 'Approved', color: 'success' as const, icon: <CheckCircleIcon fontSize="small" /> }
    if (topic.status === 'submitted' && activeMeeting?.status === 'scheduled') return { label: 'Meeting scheduled', color: 'info' as const, icon: <EventIcon fontSize="small" /> }
    if (topic.status === 'submitted' && activeMeeting?.status === 'requested') return { label: 'Meeting requested', color: 'warning' as const, icon: <HourglassIcon fontSize="small" /> }
    if (topic.status === 'submitted') return { label: 'Awaiting mentor review', color: 'warning' as const, icon: <HourglassIcon fontSize="small" /> }
    if (topic.status === 'draft' && topic.mentor_note) return { label: 'Sent back with comment', color: 'warning' as const, icon: <ChatIcon fontSize="small" /> }
    return { label: 'In progress', color: 'default' as const, icon: null }
  })()

  // Build a unified timeline: log entries + meeting transitions, in time order
  type TimelineItem = {
    when:   string
    title:  string
    detail: string | null
    icon:   React.ReactNode
    color:  'primary' | 'success' | 'warning' | 'info' | 'default'
    by:     string | null
  }
  const timeline: TimelineItem[] = []
  for (const l of logs) {
    timeline.push({
      when:   l.acted_at,
      title:  ACTION_LABEL[l.action],
      detail: l.note,
      by:     l.reviewer_name,
      icon:   l.action === 'approve'   ? <CheckCircleIcon fontSize="small" />
            : l.action === 'schedule'  ? <EventIcon fontSize="small" />
            : l.action === 'mark_held' ? <EventIcon fontSize="small" />
            :                            <ChatIcon fontSize="small" />,
      color:  l.action === 'approve' ? 'success' : 'primary',
    })
  }
  for (const m of meetings) {
    if (m.scheduled_at) {
      timeline.push({
        when:   m.scheduled_at,
        title:  m.status === 'cancelled' ? 'Meeting cancelled' : 'Meeting scheduled',
        detail: m.scheduled_note ?? null,
        by:     null,
        icon:   <EventIcon fontSize="small" />,
        color:  m.status === 'cancelled' ? 'default' : 'info',
      })
    }
  }
  for (const r of revisionRequests) {
    timeline.push({
      when:   r.requested_at,
      title:  'Revision requested',
      detail: r.reason,
      by:     null,
      icon:   <RestartAltIcon fontSize="small" />,
      color:  'warning',
    })
    if (r.decided_at) {
      timeline.push({
        when:   r.decided_at,
        title:  r.status === 'approved'
          ? 'Revision approved — Step 1 reopened for editing'
          : 'Revision request denied',
        detail: r.mentor_note,
        by:     r.mentor_name,
        icon:   r.status === 'approved' ? <CheckCircleIcon fontSize="small" /> : <CancelIcon fontSize="small" />,
        color:  r.status === 'approved' ? 'success' : 'default',
      })
    }
  }
  timeline.sort((a, b) => a.when.localeCompare(b.when))

  return (
    <Box sx={{ maxWidth: 760, py: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Mentor Review</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Track your mentor's review of your topic submission — comments, meetings, and the final approval.
      </Typography>

      {/* Headline status */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {headlineStatus.icon}
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Status
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{headlineStatus.label}</Typography>
          </Box>
          <Chip size="small" label={headlineStatus.label} color={headlineStatus.color} />
        </CardContent>
      </Card>

      {/* Approved topic */}
      {topic.status === 'completed' && topic.approvedTitle && (
        <Card variant="outlined" sx={{ mb: 3, borderColor: 'success.light', bgcolor: 'rgba(46,125,50,0.04)' }}>
          <CardContent>
            <Typography variant="caption" color="success.main" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Approved Research Topic
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, mb: 0.75 }}>
              {topic.approvedTitle}
            </Typography>
            {topic.approvedDescription && (
              <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
                {topic.approvedDescription}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Latest mentor comment — surfaced even when in 'draft' so the student
          knows what to address. For 'completed', this is the approval note. */}
      {topic.mentor_note && (
        <Card variant="outlined" sx={{ mb: 3, borderColor: 'primary.light' }}>
          <CardContent>
            <Typography variant="caption" color="primary.main" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {topic.status === 'completed' ? "Mentor's note" : 'Latest mentor feedback'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
              {topic.mentor_note}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Active meeting card — only when one is requested or scheduled */}
      {activeMeeting && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Mentor meeting
            </Typography>
            {activeMeeting.scheduled_at ? (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {fmtDateTime(activeMeeting.scheduled_at)}
                </Typography>
                {activeMeeting.scheduled_note && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                    {activeMeeting.scheduled_note}
                  </Typography>
                )}
              </>
            ) : (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.5 }}>
                  Meeting requested
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  The office is contacting your parent to find a time. You'll see the scheduled
                  meeting here once it's confirmed.
                </Typography>
                {activeMeeting.request_note && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                    Mentor's context: "{activeMeeting.request_note}"
                  </Typography>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Conversation with mentor — open while submission is under review */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Conversation with your mentor
          </Typography>
          <ConversationThread
            projectId={projectId}
            editable={topic.status === 'submitted'}
            viewerSide="student"
          />
        </CardContent>
      </Card>

      {/* Pending revision request — visible while waiting on mentor */}
      {pendingRevision && (
        <Card variant="outlined" sx={{ mb: 3, borderColor: 'warning.light', bgcolor: 'rgba(237,108,2,0.04)' }}>
          <CardContent>
            <Typography variant="caption" color="warning.main" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Revision request — awaiting mentor
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {pendingRevision.reason}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, fontStyle: 'italic' }}>
              Requested {fmtDateTime(pendingRevision.requested_at)}. Your mentor will approve or
              deny — you'll see their decision on your dashboard. If approved, Step 1 reopens
              for editing in place; your Step 2 work resets.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Request revision affordance — only after Step 1 is approved */}
      {canRequestRevision && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Want to revise Step 1?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, mb: 1.5 }}>
              If you've changed direction since Step 1 was approved, ask your mentor to reopen
              it. They'll see your reason and decide. If approved, Step 1 reopens for editing
              with your categories, topics, and finalists still in place. Your later steps
              reopen too, with your work kept, so you can revise them for your new topic.
            </Typography>
            <Button variant="outlined" size="small" onClick={() => setReqDialogOpen(true)}>
              Request revision
            </Button>
          </CardContent>
        </Card>
      )}

      <RevisionRequestDialog
        open={reqDialogOpen}
        projectId={projectId}
        studentId={studentId}
        onClose={() => setReqDialogOpen(false)}
        onSubmitted={(newReq) => {
          setRevisionRequests(prev => [...prev, newReq])
          setReqDialogOpen(false)
        }}
      />

      {/* Timeline of events */}
      {timeline.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Review history</Typography>
          <Divider sx={{ mb: 1.5 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {timeline.map((t, i) => (
              <Box key={i} sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1.5,
                p: 1.25, borderRadius: 1.5,
                bgcolor: t.color === 'success' ? 'rgba(46,125,50,0.04)' : 'background.paper',
                border: '1px solid', borderColor: 'divider',
              }}>
                <Box sx={{ color: t.color === 'success' ? 'success.main'
                                 : t.color === 'info'    ? 'info.main'
                                 : t.color === 'warning' ? 'warning.main'
                                 : 'primary.main',
                           flexShrink: 0, mt: '2px' }}>
                  {t.icon}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.title}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {fmtDateTime(t.when)}{t.by ? ` · ${t.by}` : ''}
                  </Typography>
                  {t.detail && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                      {t.detail}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  )
}

function RevisionRequestDialog({
  open, projectId, studentId, onClose, onSubmitted,
}: {
  open:        boolean
  projectId:   string
  studentId:   string
  onClose:     () => void
  onSubmitted: (req: RevisionRequestEntry) => void
}) {
  const [reason,  setReason]  = useState('')
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState<string | null>(null)

  const minChars = 30
  const reasonT  = reason.trim()
  const blocked  = reasonT.length < minChars

  function close() {
    if (loading) return
    setReason(''); setError(null)
    onClose()
  }

  async function submit() {
    if (loading || blocked) return
    setLoading(true); setError(null)
    try {
      const { data, error: insErr } = await supabase
        .from('revision_request')
        .insert({
          project_id:      projectId,
          student_id:      studentId,
          step_number:     1,
          target_subphase: null,
          reason:          reasonT,
          status:          'pending',
        })
        .select('id, step_number, target_subphase, reason, status, mentor_note, requested_at, decided_at, mentor_id')
        .single()
      if (insErr) throw new Error(insErr.message)
      onSubmitted({
        id:              (data as any).id,
        step_number:     (data as any).step_number,
        target_subphase: (data as any).target_subphase,
        reason:          (data as any).reason,
        status:          (data as any).status,
        mentor_note:     (data as any).mentor_note,
        requested_at:    (data as any).requested_at,
        decided_at:      (data as any).decided_at,
        mentor_name:     null,
      })
      setReason(''); setError(null)
    } catch (e: any) {
      setError(e.message ?? 'Could not submit request — please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Request to revise Step 1</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
          Tell your mentor why you want to reopen Step 1. If they approve, you'll be able to
          revise from the Research Category or Research Topic tab — you decide how far back to
          go when you're ready.
        </Typography>
        <TextField
          fullWidth multiline rows={4} size="small"
          label="Why do you want to revise?"
          placeholder="e.g. Once I started Step 2 I realized the topic was too broad — I'd like to pick a more specific direction."
          value={reason}
          onChange={e => setReason(e.target.value)}
          disabled={loading}
          helperText={
            blocked
              ? `${reasonT.length} / ${minChars} min — your mentor needs context to decide.`
              : 'Looks good — your mentor will see this exactly.'
          }
          error={reasonT.length > 0 && blocked}
        />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={loading || blocked}>
          {loading
            ? <><CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />Submitting…</>
            : 'Send to mentor'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
